import {
  combineLatest,
  distinctUntilChanged,
  map,
  Observable,
  startWith,
} from 'rxjs';
import { t, type I18nLocale } from '@ardoq/i18n';
import { Locale } from '@ardoq/locale';
import {
  action$,
  dispatchAction,
  extractPayload,
  ofType,
} from '@ardoq/rxbeach';
import type { QueryBuilderQuery } from '@ardoq/api-types';
import type { EnhancedScopeData } from '@ardoq/data-model';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import {
  dynamicReferenceCreation$,
  DynamicReferenceCreationState,
} from './dynamicReferenceCreation$';
import { sourceTriples$, SourceTriples } from './sourceTriples$';
import {
  dynamicReferenceCreationSearchScopeDataLoaded,
  dynamicReferenceCreationSearchTermChanged,
} from './dynamicReferenceCreationActions';
import {
  MIN_FILTER_TERM_LENGTH,
  type NoodleSearchState,
  toNoodleSearchState,
  toTargetTypeNames,
} from './noodleSearchStateUtils';

export type NoodleSearchBoxProps = {
  isVisible: boolean;
};

export type NoodleSearchPaneProps = {
  isIdle: boolean;
  searchLabel: string;
  placeholder: string;
  searchHintMessage: string | undefined;
  locale: Locale;
  i18nLocale: I18nLocale;
  onScopeDataLoaded: (scopeData: EnhancedScopeData) => void;
  filterTerm: string;
  onFilterTermChange: (filterTerm: string) => void;
  query: QueryBuilderQuery | null;
};

type ToNoodleSearchPanePropsArgs = {
  targetTypeNames: string[] | null;
  filterTerm: string;
  locale: Locale;
  i18nLocale: I18nLocale;
};

const toSourceTargetTypeNames = (
  sourceTriples: SourceTriples | null
): string[] | null => {
  if (!sourceTriples) return null;
  return toTargetTypeNames(
    sourceTriples.triples,
    sourceTriples.constraintsEnforcementMode
  );
};

const targetTypeNames$: Observable<string[] | null> = sourceTriples$.pipe(
  startWith(null),
  map(toSourceTargetTypeNames)
);

const filterTerm$: Observable<string> = action$.pipe(
  ofType(dynamicReferenceCreationSearchTermChanged),
  extractPayload(),
  startWith('')
);

const onScopeDataLoaded = (scopeData: EnhancedScopeData) =>
  dispatchAction(dynamicReferenceCreationSearchScopeDataLoaded(scopeData));

const onFilterTermChange = (filterTerm: string) =>
  dispatchAction(dynamicReferenceCreationSearchTermChanged(filterTerm));

const toSearchHintMessage = (
  searchState: NoodleSearchState,
  i18nLocale: I18nLocale
): string | undefined => {
  switch (searchState.kind) {
    case 'loadingTargetTypes':
      return t(
        i18nLocale,
        'dynamicReferenceCreation:searchLoadingTargetTypesHint',
        'Preparing search...'
      );
    case 'noTargetTypes':
      return t(
        i18nLocale,
        'dynamicReferenceCreation:searchNoTargetTypesHint',
        "The metamodel doesn't allow references from this component type"
      );
    case 'termTooShort':
      return t(
        i18nLocale,
        'dynamicReferenceCreation:searchMinCharactersHint',
        { count: MIN_FILTER_TERM_LENGTH },
        'Type at least {{count}} characters to search'
      );
    case 'searching':
      return undefined;
  }
};

const toQuery = (searchState: NoodleSearchState): QueryBuilderQuery | null =>
  searchState.kind === 'searching' ? searchState.query : null;

const toNoodleSearchPaneProps = ({
  targetTypeNames,
  filterTerm,
  locale,
  i18nLocale,
}: ToNoodleSearchPanePropsArgs): NoodleSearchPaneProps => {
  const searchState = toNoodleSearchState(filterTerm, targetTypeNames);
  return {
    isIdle: searchState.kind !== 'searching',
    searchLabel: t(
      i18nLocale,
      'dynamicReferenceCreation:searchComponentsAriaLabel',
      'Search for components'
    ),
    placeholder: t(
      i18nLocale,
      'dynamicReferenceCreation:searchComponentsPlaceholder',
      'Search components...'
    ),
    searchHintMessage: toSearchHintMessage(searchState, i18nLocale),
    locale,
    i18nLocale,
    onScopeDataLoaded,
    filterTerm,
    onFilterTermChange,
    query: toQuery(searchState),
  };
};

export const noodleSearchPaneProps$: Observable<NoodleSearchPaneProps> =
  combineLatest({
    targetTypeNames: targetTypeNames$,
    filterTerm: filterTerm$,
    locale: locale$,
    i18nLocale: i18nLocale$,
  }).pipe(map(toNoodleSearchPaneProps));

const isDragging = (gesture: DynamicReferenceCreationState): boolean =>
  gesture.kind === 'dragging';

const toNoodleSearchBoxProps = (isVisible: boolean): NoodleSearchBoxProps => ({
  isVisible,
});

export const noodleSearchBoxProps$: Observable<NoodleSearchBoxProps> =
  dynamicReferenceCreation$.pipe(
    map(isDragging),
    distinctUntilChanged(),
    map(toNoodleSearchBoxProps)
  );
